/**
 * Page setup CRUD (docs/opencadstudio-parity/02-plot-pdf-ctb.md §3.3).
 * Each CAD layout carries one PageSetup (useLayoutStore.pageSetup); new
 * layouts default to ISO A4 landscape at 1:1 with plotType 'layout' — the
 * same defaults OCS applies in apply_default_page_setup. PageSetup is a
 * plain JSON object, so it rides the existing .yap cadScene envelope
 * (ProjectFileIO restoreCadSideState → useLayoutStore.replaceAll) without
 * any codec change.
 */

import { PAPER_SIZES, type CadLayout } from '../../state/useLayoutStore';
import { DEFAULT_PLOT_OPTIONS, type PageSetup, type PlotAreaKind } from './types';

/** Millimetres per drawing unit per unit interpretation. */
export const UNIT_TO_MM: Record<PageSetup['drawingUnits'], number> = {
    mm: 1,
    cm: 10,
    m: 1000,
    in: 25.4,
};

/** Standard engineering scale list (num:den, paper per model). */
export const STANDARD_SCALES: { num: number; den: number }[] = [
    { num: 1, den: 1 }, { num: 1, den: 2 }, { num: 1, den: 5 },
    { num: 1, den: 10 }, { num: 1, den: 20 }, { num: 1, den: 25 },
    { num: 1, den: 50 }, { num: 1, den: 100 }, { num: 1, den: 200 },
    { num: 1, den: 500 }, { num: 1, den: 1000 },
    { num: 2, den: 1 }, { num: 5, den: 1 }, { num: 10, den: 1 },
];

/** Paper choices offered by the dialogs: PAPER_SIZES plus custom mm. */
export function paperSizeMeters(id: string): { w: number; h: number } | null {
    const hit = PAPER_SIZES[id];
    return hit ? { w: hit.w, h: hit.h } : null;
}

/** Landscape mm dimensions for a paper id (w > h). */
export function paperSizeMm(id: string): { w: number; h: number } {
    const s = paperSizeMeters(id) ?? paperSizeMeters('A4')!;
    return { w: Math.round(s.w * 1000), h: Math.round(s.h * 1000) };
}

/** Fresh default setup — A4 landscape, 1:1, plot the layout, no plotter. */
export function applyDefaultPageSetup(name = 'Layout1'): PageSetup {
    const mm = paperSizeMm('A4');
    return {
        name,
        paperName: 'ISO A4 (297.00 x 210.00 mm)',
        paperWidth: mm.w,
        paperHeight: mm.h,
        units: 'mm',
        rotation: 0,
        fitToPaper: false,
        scaleNum: 1,
        scaleDen: 1,
        drawingUnits: 'm',
        offsetX: 0,
        offsetY: 0,
        centerPlot: true,
        plotStyleName: '',
        plotType: 'layout',
        options: { ...DEFAULT_PLOT_OPTIONS },
    };
}

/**
 * Setup of a layout, lazily defaulted: layouts saved before Faz 2 (and the
 * model-space pseudo page) have no stored setup — they plot with the OCS
 * defaults instead of failing.
 */
export function layoutPageSetup(layout: Pick<CadLayout, 'name' | 'pageSetup'> | null | undefined): PageSetup {
    if (layout?.pageSetup) return layout.pageSetup;
    return applyDefaultPageSetup(layout?.name ?? 'Layout1');
}

/**
 * Keep a layout's drawing-canvas paper fields (meters, used by the on-screen
 * paper renderer) in step with the setup's sheet size. The setup is the
 * authoritative page model; the canvas is derived.
 */
export function syncLayoutPaper(layout: CadLayout, setup: PageSetup): { paper: CadLayout['paper']; width: number; height: number } {
    // Sheet portrait/landscape determines the canvas footprint; the canvas
    // keeps the natural (unrotated) paper id where possible.
    const natural = naturalPaperId(setup.paperWidth, setup.paperHeight);
    const w = setup.paperWidth / 1000;
    const h = setup.paperHeight / 1000;
    return {
        paper: (natural ?? layout.paper) as CadLayout['paper'],
        width: Math.max(w, h),
        height: Math.min(w, h),
    };
}

/** Match mm dimensions back onto a PAPER_SIZES id (landscape compare). */
export function naturalPaperId(widthMm: number, heightMm: number): string | null {
    const w = Math.max(widthMm, heightMm), h = Math.min(widthMm, heightMm);
    for (const [id, size] of Object.entries(PAPER_SIZES)) {
        const sw = size.w * 1000, sh = size.h * 1000;
        if (Math.abs(Math.max(sw, sh) - w) < 1 && Math.abs(Math.min(sw, sh) - h) < 1) return id;
    }
    return null;
}

/** scale label '1:100' / '2:1' / '100:3' for a setup. */
export function scaleLabel(num: number, den: number): string {
    const n = num > 0 ? num : 1, d = den > 0 ? den : 1;
    return `${n}:${d}`;
}

/**
 * Effective millimetres per source unit for a setup. Layout plots are
 * pinned to 1:1 paper (the sheet IS the paper space); model plots use
 * fit-to-paper or the num:den ratio carried through the drawing-unit
 * interpretation (metres drawing → 1:100 = 10 mm per model unit).
 */
export function effectiveScaleMmPerUnit(
    setup: PageSetup,
    fitBounds: { w: number; h: number } | null,
): { scale: number; fit: boolean } {
    if (setup.plotType === 'layout') return { scale: UNIT_TO_MM.m, fit: false };
    if (setup.fitToPaper && fitBounds && fitBounds.w > 0 && fitBounds.h > 0) {
        // OCS fit-to-paper: bounds inflated by 1.05, sheet fully used.
        const sx = (setup.paperWidth / fitBounds.w) / 1.05;
        const sy = (setup.paperHeight / fitBounds.h) / 1.05;
        return { scale: Math.max(1e-6, Math.min(sx, sy)), fit: true };
    }
    const n = setup.scaleNum > 0 ? setup.scaleNum : 1;
    const d = setup.scaleDen > 0 ? setup.scaleDen : 1;
    const unitMm = UNIT_TO_MM[setup.drawingUnits] ?? UNIT_TO_MM.m;
    return { scale: unitMm * (n / d), fit: false };
}

/** Normalize a user window (any corner order) into min/max. */
export function normalizeWindow(win: { x1: number; y1: number; x2: number; y2: number }) {
    return {
        minX: Math.min(win.x1, win.x2), minY: Math.min(win.y1, win.y2),
        maxX: Math.max(win.x1, win.x2), maxY: Math.max(win.y1, win.y2),
    };
}

/** Validate a partial patch coming from dialog state. */
export function patchPageSetup(base: PageSetup, patch: Partial<PageSetup>): PageSetup {
    const next: PageSetup = { ...base, ...patch, options: { ...base.options, ...(patch.options ?? {}) } };
    next.scaleNum = Math.max(1e-6, next.scaleNum || 1);
    next.scaleDen = Math.max(1e-6, next.scaleDen || 1);
    const type: PlotAreaKind = next.plotType;
    if (!['layout', 'extents', 'window', 'display'].includes(type)) next.plotType = 'layout';
    return next;
}
